import { Chess, PAWN, QUEEN, swapColor, } from "@chesshotel/chess";
import { ChessTermType, MoveType, } from "@chesshotel/types";
import { detectPins } from "./tactics/pin.js";
export const files = "abcdefgh".split("");
export const invFiles = "hgfedcba".split("");
export const ranks = [1, 2, 3, 4, 5, 6, 7, 8];
export const invRanks = [8, 7, 6, 5, 4, 3, 2, 1];
export function pos2key(pos) {
    return files[pos[0] - 1] + pos[1];
}
export function key2pos(pos) {
    return [files.indexOf(pos[0]) + 1, parseInt(pos[1])];
}
export function key2rank(pos) {
    return parseInt(pos.substring(1));
}
export function distance(pos1, pos2) {
    return Math.sqrt(Math.pow(pos1[0] - pos2[0], 2) + Math.pow(pos1[1] - pos2[1], 2));
}
const KING_VALUE = 100;
export const pieceValues = {
    p: 1,
    n: 3,
    b: 3,
    r: 5,
    q: 9,
    k: KING_VALUE,
};
export function uci2move(move) {
    const moveObj = {
        from: move.slice(0, 2),
        to: move.slice(2, 4),
    };
    const promotion = move.slice(4, 5).toLowerCase();
    if (promotion) {
        moveObj.promotion = promotion;
    }
    return moveObj;
}
export function move2short(move) {
    const { from, to, promotion } = move;
    return { from, to, promotion };
}
export function move2uci(move) {
    return (move.from +
        move.to +
        (move.promotion ? move.promotion.toUpperCase() : ""));
}
export function moveObj(move) {
    const rank = key2rank(move.to);
    const promotion = rank == 1 || rank == 8 ? QUEEN : null;
    if (promotion) {
        move.promotion = promotion;
    }
    return move;
}
export function moveEquals(move1, move2) {
    return (move1.from == move2.from &&
        move1.to == move2.to &&
        move1.promotion == move2.promotion);
}
export function getFen(startFen, move, move2 = null) {
    const chess = new Chess(startFen);
    chess.move(uci2move(move));
    if (move2) {
        chess.move(uci2move(move2));
    }
    return chess.fen();
}
export function sortPiecesByMaxValue(pieces) {
    return pieces.sort((a, b) => pieceValues[a.type] - pieceValues[b.type]);
}
export function sortPiecesByMaxValueDesc(pieces) {
    return pieces.sort((a, b) => pieceValues[b.type] - pieceValues[a.type]);
}
export function getSortedMovesToSquare(chess, square, filteredByLoss) {
    const movesToSquare = chess
        .uciMoves({ legal: true })
        .map((move) => uci2move(move))
        .filter((move) => move.to == square);
    let filteredByLossMoves = movesToSquare;
    if (filteredByLoss) {
        const fen = chess.fen();
        const color = chess.turn();
        const pins = detectPins(fen, color);
        const targetPiece = chess.get(square);
        const baseCaptureValue = targetPiece ? pieceValues[targetPiece.type] : 0;
        const scored = movesToSquare.map((move) => {
            const piece = chess.get(move.from);
            let promotionGain = 0;
            if (piece && piece.type === PAWN) {
                const rank = key2rank(move.to);
                if ((color === "w" && rank === 8) || (color === "b" && rank === 1)) {
                    promotionGain = pieceValues[QUEEN] - pieceValues[PAWN];
                }
            }
            const pin = pins.find((p) => p.attackedPiece.square == move.from);
            let pinLoss = 0;
            if (pin && pin.mainPiece.square !== move.to) {
                pinLoss = pin.pinMoveGain;
            }
            const delta = baseCaptureValue + promotionGain + pinLoss;
            return { piece, fromSquare: move.from, delta };
        });
        const filtered = scored.filter((s) => s.delta >= 0);
        const ordered = filtered.sort((a, b) => b.delta - a.delta);
        return ordered.map(({ piece, fromSquare }) => ({
            ...piece,
            square: fromSquare,
        }));
    }
    return filteredByLossMoves
        .map((move) => {
        const piece = chess.get(move.from);
        return { ...piece, square: move.from };
    })
        .sort((a, b) => pieceValues[a.type] - pieceValues[b.type]);
}
function getSmallestAttackers(chess, square, filteredByLoss) {
    const attackers = getSortedMovesToSquare(chess, square, filteredByLoss);
    if (attackers.length === 0) {
        return null;
    }
    return attackers;
}
function getPossibleMoves(chosenMove, pieces, square, lastMoveBranch = null) {
    const possibleMoves = pieces.map((piece) => moveObj({ from: piece.square, to: square }));
    if (lastMoveBranch == null) {
        return possibleMoves;
    }
    const inLastMoveBranch = lastMoveBranch.findIndex((move) => move.from === chosenMove.from && move.to === chosenMove.to);
    if (inLastMoveBranch !== -1) {
        lastMoveBranch.splice(inLastMoveBranch, 1);
        return lastMoveBranch.concat(possibleMoves);
    }
    else {
        return possibleMoves;
    }
}
const exchangeEvaluationCache = new Map();
const EXCHANGE_EVAL_CACHE_CAP = 5000;
export function clearExchangeEvaluationCache() {
    exchangeEvaluationCache.clear();
}
export function exchangeEvaluation(fen, square, options) {
    const cacheKey = JSON.stringify({
        fen,
        square,
        options: options || {},
    });
    const cached = exchangeEvaluationCache.get(cacheKey);
    if (cached) {
        return cached;
    }
    const chess = new Chess(fen);
    const firstColor = chess.turn();
    const capturesAttacker = [];
    const capturesDefender = [];
    function see(square, from, isRoot = false, lastPossibleMoves = null) {
        let chosenMove = null;
        let possibleMoves;
        let rootPinLoss = 0;
        if (from) {
            chosenMove = moveObj({ from, to: square });
            possibleMoves = [chosenMove];
            if (isRoot) {
                const color = chess.turn();
                const pins = detectPins(chess.fen(), color);
                const pin = pins.find((p) => p.attackedPiece.square === from);
                if (pin && chosenMove.to !== pin.mainPiece.square) {
                    rootPinLoss = pin.pinMoveGain;
                }
            }
        }
        else {
            const pieces = getSmallestAttackers(chess, square, options?.filteredByLoss);
            if (pieces) {
                const firstPiece = pieces[0];
                chosenMove =
                    firstPiece && moveObj({ from: firstPiece.square, to: square });
                possibleMoves = getPossibleMoves(chosenMove, pieces, square, lastPossibleMoves);
            }
        }
        if (!chosenMove)
            return { value: 0, moves: [], gains: [], movesTree: [] };
        let moveResult = null;
        try {
            moveResult = chess.move(chosenMove);
        }
        catch (e) {
            return { value: 0, moves: [], gains: [], movesTree: [] };
        }
        const capturedType = moveResult.captured;
        const capturedValue = pieceValues[capturedType] || 0;
        const promotionValue = moveResult.promotion
            ? pieceValues[moveResult.promotion] - pieceValues[PAWN]
            : 0;
        const response = see(square, undefined, false, possibleMoves);
        const rawGain = capturedValue +
            promotionValue -
            response.value -
            (isRoot ? rootPinLoss : 0);
        const currentValue = isRoot ? rawGain : Math.max(0, rawGain);
        chess.undo();
        if (!isRoot && rawGain < 0) {
            return { value: 0, moves: [], gains: [], movesTree: [] };
        }
        const acceptedMoves = [chosenMove, ...response.moves];
        const acceptedMoveTree = [
            possibleMoves,
            ...response.movesTree,
        ];
        const acceptedGains = [currentValue, ...response.gains];
        return {
            value: currentValue,
            moves: acceptedMoves,
            movesTree: acceptedMoveTree,
            gains: acceptedGains,
        };
    }
    const root = see(square, options?.from ?? null, true);
    if (root.moves.length > 0) {
        const sim = new Chess(fen);
        let mover = sim.turn();
        for (const m of root.moves) {
            const res = sim.move(moveObj(m));
            if (mover === firstColor && res?.captured) {
                capturesAttacker.push(res.captured);
            }
            if (mover !== firstColor && res.captured) {
                capturesDefender.push(res.captured);
            }
            mover = sim.turn();
        }
    }
    let pieceWin = null;
    if (root.value != 0) {
        pieceWin = getPieceWin(capturesAttacker, capturesDefender, root.value);
    }
    const result = {
        gain: root.value,
        moveSequence: root.moves,
        movesTree: root.movesTree,
        pieceWin: pieceWin,
        gainSequence: root.gains,
    };
    if (exchangeEvaluationCache.size > EXCHANGE_EVAL_CACHE_CAP) {
        exchangeEvaluationCache.clear();
    }
    exchangeEvaluationCache.set(cacheKey, result);
    return result;
}
export function getPieceWin(capturesAttacker, capturesDefender, gain) {
    let winCaptures;
    let loseCaptures;
    if (gain < 0) {
        winCaptures = capturesDefender;
        loseCaptures = capturesAttacker;
    }
    else {
        winCaptures = capturesAttacker;
        loseCaptures = capturesDefender;
    }
    winCaptures.sort((a, b) => pieceValues[b] - pieceValues[a]);
    loseCaptures.sort((a, b) => pieceValues[b] - pieceValues[a]);
    for (let i = 0; i < winCaptures.length; i++) {
        const winCapturesValue = pieceValues[winCaptures[i]];
        let current = 0;
        let removeCount = 0;
        for (let j = 0; j < loseCaptures.length; j++) {
            const loseCapturesValue = pieceValues[loseCaptures[j]];
            if (current == 0 && winCapturesValue === loseCapturesValue) {
                winCaptures.splice(i, 1);
                loseCaptures.splice(j, 1);
                i--;
                j--;
                current = 0;
                removeCount = 0;
                break;
            }
            if (current != 0 && winCapturesValue == loseCapturesValue + current) {
                winCaptures.splice(i, 1);
                loseCaptures.splice(j - removeCount, removeCount + 1);
                i--;
                j--;
                current = 0;
                removeCount = 0;
                break;
            }
            if (loseCapturesValue < winCapturesValue) {
                current += loseCapturesValue;
            }
            removeCount++;
        }
    }
    if (winCaptures.length > 0) {
        return winCaptures[0];
    }
}
export function detectHangingPieces(fen, color) {
    const chess = new Chess(fen);
    const opponentColor = swapColor(color);
    const board = chess.board();
    const hangingPieces = [];
    for (let file = 0; file < 8; file++) {
        for (let rank = 0; rank < 8; rank++) {
            const piece = board[rank][file];
            if (piece && piece.color === opponentColor) {
                const attackers = chess.attackers(piece.square, color);
                const isAttacked = attackers.length > 0;
                if (!isAttacked) {
                    continue;
                }
                const defenders = chess.attackers(piece.square, opponentColor);
                const isDefended = defenders.length > 0;
                let attackerLowerValue = false;
                for (let attackerSquare of attackers) {
                    const attacker = chess.get(attackerSquare);
                    if (attacker) {
                        if (pieceValues[attacker.type] < pieceValues[piece.type]) {
                            attackerLowerValue = true;
                        }
                    }
                }
                if (isAttacked && (!isDefended || attackerLowerValue)) {
                    const evaluation = exchangeEvaluation(fen, piece.square, {
                        backtrackBadMoves: true,
                        filteredByLoss: true,
                    });
                    if (evaluation.gain > 0) {
                        hangingPieces.push(piece);
                    }
                }
            }
        }
    }
    return hangingPieces;
}
export function getThreats(fen, threatenedColor) {
    const chess = new Chess(fen);
    if (chess.inCheck()) {
        return [];
    }
    else if (chess.turn() == threatenedColor) {
        chess.changeTurn();
    }
    const captures = chess.captures();
    const threats = captures
        .map((moveStr) => {
        const move = uci2move(moveStr);
        const evaluation = exchangeEvaluation(chess.fen(), move.to, {
            from: move.from,
            backtrackBadMoves: true,
            filteredByLoss: true,
        });
        const attack = {
            from: chess.getBoardPiece(move.from),
            to: chess.getBoardPiece(move.to),
        };
        return { move, attack, evaluation };
    })
        .filter(({ evaluation }) => {
        return evaluation.gain > 0;
    })
        .map((threat) => {
        return { ...threat.attack, gain: threat.evaluation.gain };
    })
        .sort((a, b) => b.gain - a.gain);
    return threats;
}
export function getThreats2(fen, threatenedColor) {
    const chess = new Chess(fen);
    if (chess.inCheck()) {
        return [];
    }
    else if (chess.turn() == threatenedColor) {
        chess.changeTurn();
    }
    const captures = chess
        .moves({ legal: true, verbose: true })
        .filter((move) => move.captured);
    const threats = captures
        .map((move) => {
        const evaluation = exchangeEvaluation(chess.fen(), move.to, {
            from: move.from,
            backtrackBadMoves: true,
        });
        const attack = {
            from: chess.getBoardPiece(move.from),
            to: chess.getBoardPiece(move.to),
        };
        return { move, attack, evaluation };
    })
        .filter(({ evaluation }) => {
        return evaluation.gain > 0;
    })
        .map((threat) => {
        return { ...threat.attack, gain: threat.evaluation.gain };
    });
    return threats;
}
export function getNewThreat(fen, move) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const threats = getThreats(fen, opponentColor);
    chess.move(uci2move(move));
    const threatsAfter = getThreats(chess.fen(), opponentColor);
    const newThreats = threatsAfter
        .filter((threat) => {
        return !threats.find((t) => t.from.square == threat.from.square && t.to.square == threat.to.square);
    })
        .sort((a, b) => b.gain - a.gain);
    return newThreats.length > 0 ? newThreats[0] : null;
}
export function isNegativeMove(position) {
    return (position.moveType === MoveType.BLUNDER ||
        position.moveType === MoveType.MISTAKE ||
        position.moveType === MoveType.INACCURACY);
}
export function isThreat(term) {
    return (term === ChessTermType.THREATEN_PIECE ||
        term === ChessTermType.KICK_PIECE ||
        term === ChessTermType.TEMPO);
}
export function isTactic(term) {
    return (term === ChessTermType.TACTIC ||
        term === ChessTermType.FORK ||
        term === ChessTermType.PIN ||
        term === ChessTermType.SKEWER ||
        term === ChessTermType.DOUBLE_ATTACK ||
        term === ChessTermType.DISCOVERED_ATTACK);
}
export function getActionTypes(fen, pvMoves) {
    const chess = new Chess(fen);
    const actions = [];
    let firstCaptureIndex = null;
    let moveResult = null;
    for (let i = 0; i < pvMoves.length; i++) {
        const move = pvMoves[i];
        const fen = chess.fen();
        try {
            moveResult = chess.move(uci2move(move));
        }
        catch {
            pvMoves = pvMoves.slice(0, i);
            break;
        }
        if (!moveResult) {
            pvMoves = pvMoves.slice(0, i);
            break;
        }
        if ((moveResult?.captured || moveResult?.promotion) &&
            firstCaptureIndex === null) {
            firstCaptureIndex = i;
        }
        if (moveResult?.captured || moveResult?.promotion) {
            actions.push("capture");
        }
        else if (chess.inCheck()) {
            actions.push("forcing");
        }
        else {
            const newThreat = getNewThreat(fen, move);
            if (newThreat && newThreat.gain > 1) {
                actions.push("forcing");
            }
            else {
                actions.push("none");
            }
        }
    }
    if (moveResult &&
        actions.length > 10 &&
        actions[actions.length - 1] == "capture") {
        const evalResult = exchangeEvaluation(chess.fen(), moveResult.to, {
            backtrackBadMoves: true,
        });
        if (evalResult.gain > 0) {
            evalResult.moveSequence.forEach((move) => {
                actions.push("capture");
                pvMoves.push(move2uci(move));
            });
        }
    }
    return { actions, pvMoves, firstCaptureIndex };
}
//# sourceMappingURL=util.js.map